import { useEffect, useRef, useState } from "react";
import { createDashboardLogger, DashboardModuleNames } from "@/logging";
import { ensureStreamData } from "./ensure-stream-data";
import type { RequestedStream } from "./types";

const Logger = createDashboardLogger(DashboardModuleNames.dataViewer);

/** React wrapper around `ensureStreamData`. Re-runs on input change only; returns `isFetching`. See README.md. */
export function useEnsureStreamData({
    streams,
    startMs,
    endMs,
}: {
    streams: RequestedStream[];
    startMs: number;
    endMs: number;
}): boolean {
    const [isFetching, setIsFetching] = useState(true);

    // Only the latest run should clear isFetching.
    const iterationRef = useRef(0);

    const streamsKey = streams
        .map((s) => `${s.streamId}:${s.resolution}`)
        .sort()
        .join(",");

    useEffect(() => {
        iterationRef.current += 1;
        const generation = iterationRef.current;
        const abortController = new AbortController();
        setIsFetching(true);

        // Pass the current time so ensureStreamData knows what's "live" vs historical.
        // Per-stream errors are handled inside ensureStreamData; this catch is just a safety net.
        ensureStreamData({ streams, startMs, endMs, now: Date.now(), signal: abortController.signal })
            .catch((err) => {
                Logger.error({ err }, "ensureStreamData failed unexpectedly");
            })
            .finally(() => {
                if (iterationRef.current === generation) setIsFetching(false);
            });

        // When deps change, abort so a slow fetch from the previous run can't write to the cache.
        return () => {
            abortController.abort();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps -- streamsKey stabilizes streams
    }, [streamsKey, startMs, endMs]);

    return isFetching;
}
